/**
 * Two-step member removal confirmation dialog.
 * Step 1: Explains impact of removal
 * Step 2: Requires typing member name to confirm
 */

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { TeamMember } from '../../types/company';

interface MemberRemovalDialogProps {
  isOpen: boolean;
  onClose: () => void;
  member: TeamMember;
  // Receives the admin's reason text. Backend requires ≥10 chars; the dialog
  // enforces the same locally so submit is disabled until valid.
  onConfirm: (reason: string) => Promise<void>;
}

type DialogStep = 'explain' | 'confirm';

const MIN_REASON_LENGTH = 10;

export default function MemberRemovalDialog({
  isOpen,
  onClose,
  member,
  onConfirm,
}: MemberRemovalDialogProps) {
  const { t, i18n } = useTranslation('team');
  const isRTL = i18n.language === 'ar';
  const [step, setStep] = useState<DialogStep>('explain');
  const [typedName, setTypedName] = useState('');
  const [reason, setReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state when dialog opens
  useEffect(() => {
    if (isOpen) {
      setStep('explain');
      setTypedName('');
      setReason('');
      setIsLoading(false);
      setError(null);
    }
  }, [isOpen]);

  // Lock body scroll and handle ESC key
  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isLoading) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose, isLoading]);

  if (!isOpen) return null;

  // Name validation - case-insensitive, trimmed
  const nameMatches =
    typedName.trim().toLowerCase() === member.displayName.toLowerCase();
  const reasonTrimmed = reason.trim();
  const reasonValid = reasonTrimmed.length >= MIN_REASON_LENGTH;
  const canSubmit = nameMatches && reasonValid;

  const handleContinue = () => {
    setStep('confirm');
  };

  const handleBack = () => {
    setStep('explain');
    setTypedName('');
    setReason('');
    setError(null);
  };

  const handleRemove = async () => {
    if (!canSubmit) return;

    setIsLoading(true);
    setError(null);

    try {
      await onConfirm(reasonTrimmed);
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to remove member';
      setError(message);
      setIsLoading(false);
    }
  };

  const dialogContent = (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      {/* Backdrop click to close */}
      <div
        className="absolute inset-0 cursor-pointer"
        aria-hidden="true"
        onClick={isLoading ? undefined : onClose}
      />

      {/* Dialog */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="removal-dialog-title"
        className="relative z-[1] w-full max-w-md overflow-hidden rounded-xl bg-white shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b p-6">
          <h2
            id="removal-dialog-title"
            className="text-lg font-semibold text-[var(--pv-color-primary)]"
          >
            {step === 'explain'
              ? t('removeMemberTitle')
              : t('removeMemberConfirmTitle')}
          </h2>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="text-gray-400 transition-colors hover:text-gray-600 cursor-pointer disabled:opacity-50"
            aria-label={isRTL ? 'إغلاق النافذة' : 'Close dialog'}
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {step === 'explain' ? (
            /* Step 1: Explain impact */
            <div className="space-y-4">
              {/* Warning icon */}
              <div className="flex justify-center">
                <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center">
                  <i className="ri-user-unfollow-line text-3xl text-amber-600" />
                </div>
              </div>

              {/* Member info */}
              <div className="text-center">
                <p className="font-medium text-gray-900">{member.displayName}</p>
                <p className="text-sm text-gray-500">{member.email}</p>
              </div>

              {/* Impact explanation */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800 mb-3">
                  {t('removeMemberWarning', { name: member.displayName })}
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2 text-sm text-amber-800">
                    <i className="ri-close-circle-line text-amber-600 mt-0.5 flex-shrink-0" />
                    <span>{isRTL ? 'سيتم إلغاء وصولهم إلى عقارات الشركة' : 'Revoke their access to company properties'}</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-amber-800">
                    <i className="ri-arrow-right-circle-line text-amber-600 mt-0.5 flex-shrink-0" />
                    <span>{isRTL
                      ? 'سيتم نقل جميع عقاراتهم إلى الشركة (المالك الجديد: مالك الشركة)'
                      : 'All their listings will be transferred to the company (new owner: company owner)'}</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-amber-800">
                    <i className="ri-mail-line text-amber-600 mt-0.5 flex-shrink-0" />
                    <span>{isRTL
                      ? 'سيستلمون بريداً إلكترونياً ثنائي اللغة يوضح سبب الإزالة'
                      : 'They will receive a bilingual email explaining the reason for removal'}</span>
                  </li>
                </ul>
              </div>
            </div>
          ) : (
            /* Step 2: Confirm with name */
            <div className="space-y-4">
              {/* Member info */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                {member.avatarUrl ? (
                  <img
                    src={member.avatarUrl}
                    alt={member.displayName}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                    <span className="text-gray-600 font-medium text-sm">
                      {member.displayName.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                <div>
                  <p className="font-medium text-gray-900">{member.displayName}</p>
                  <p className="text-sm text-gray-500">{member.email}</p>
                </div>
              </div>

              {/* Reason — required, ≥10 chars. Goes into the bilingual email
                  the removed member receives. Inline ar/en strings here
                  while the team translation file gets caught up. */}
              <div>
                <label
                  htmlFor="removal-reason"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {isRTL ? 'سبب الإزالة' : 'Reason for removal'}
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <textarea
                  id="removal-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  disabled={isLoading}
                  placeholder={isRTL
                    ? 'سيتم تضمين هذا السبب في البريد الإلكتروني الذي يستلمه العضو'
                    : 'This reason will be included in the email the member receives'}
                  rows={3}
                  minLength={MIN_REASON_LENGTH}
                  maxLength={2000}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 disabled:opacity-50 disabled:bg-gray-100 resize-vertical"
                  autoFocus
                />
                <div className="mt-1 flex justify-between text-xs">
                  <span className={reasonTrimmed.length > 0 && !reasonValid ? 'text-red-500' : 'text-gray-500'}>
                    {reasonTrimmed.length < MIN_REASON_LENGTH
                      ? (isRTL
                          ? `يجب أن يكون السبب ${MIN_REASON_LENGTH} أحرف على الأقل (${reasonTrimmed.length}/${MIN_REASON_LENGTH})`
                          : `Reason must be at least ${MIN_REASON_LENGTH} characters (${reasonTrimmed.length}/${MIN_REASON_LENGTH})`)
                      : (isRTL ? 'تم' : 'OK')}
                  </span>
                  <span className="text-gray-400">{reasonTrimmed.length}/2000</span>
                </div>
              </div>

              {/* Name confirmation input */}
              <div>
                <label
                  htmlFor="confirm-name"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {t('removeMemberConfirmPrompt', { name: member.displayName })}
                </label>
                <input
                  id="confirm-name"
                  type="text"
                  value={typedName}
                  onChange={(e) => setTypedName(e.target.value)}
                  disabled={isLoading}
                  placeholder={member.displayName}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 disabled:opacity-50 disabled:bg-gray-100"
                  autoComplete="off"
                />
              </div>

              {/* Validation hint */}
              {typedName.length > 0 && !nameMatches && (
                <p className="text-sm text-gray-500">
                  {isRTL
                    ? `يجب أن يطابق الاسم تماماً: "${member.displayName}"`
                    : `Name must match exactly: "${member.displayName}"`}
                </p>
              )}

              {/* Error display */}
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t bg-gray-50 px-6 py-4">
          {step === 'explain' ? (
            <>
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 cursor-pointer"
              >
                {t('cancel')}
              </button>
              <button
                onClick={handleContinue}
                className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 cursor-pointer"
              >
                {isRTL ? 'متابعة' : 'Continue'}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleBack}
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 cursor-pointer"
              >
                {isRTL ? 'رجوع' : 'Back'}
              </button>
              <button
                onClick={handleRemove}
                disabled={isLoading || !canSubmit}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 cursor-pointer"
              >
                {isLoading ? (
                  <>
                    <i className="ri-loader-4-line mr-2 animate-spin inline-block" />
                    {isRTL ? 'جاري الإزالة...' : 'Removing...'}
                  </>
                ) : (
                  t('removeMember')
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );

  if (typeof document !== 'undefined') {
    return createPortal(dialogContent, document.body);
  }

  return dialogContent;
}
